/**
 * Military Activity Layer — aggregates military flights and vessel positions
 * into grid-based activity zones.
 *
 * Groups events by 1-degree lat/lng grid cells and renders each cell as a
 * filled polygon with opacity proportional to activity density. This shows
 * concentrations of military-related movements without overwhelming the map
 * with individual markers.
 *
 * Filters:
 * - Flight positions with military callsigns or tags
 * - Vessel positions with military type or naval tags
 * - Conflict events with military classification
 */

import { PolygonLayer, ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';

/** Layer IDs handled by this renderer. */
export const MILITARY_ACTIVITY_LAYER_IDS = new Set(['military-activity']);

/** Military-related keywords for filtering events. */
const MILITARY_KEYWORDS = new Set([
  'military', 'navy', 'naval', 'air force', 'armed', 'defense',
  'defence', 'army', 'marine', 'coast guard', 'fighter', 'bomber',
  'tanker', 'reconnaissance', 'awacs', 'patrol', 'warship',
]);

/** Check if an event is military-related. */
export function isMilitaryEvent(event: NormalizedEvent): boolean {
  // Direct military types
  if (event.type === 'military') return true;

  const raw = event.raw as Record<string, unknown> | undefined;

  // Position events classify by their OWN attributes only — the keyword
  // list must never touch them: vessel events are tagged with their cargo
  // category ('tanker' means an oil tanker there, not a tanker aircraft).
  if (event.type === 'vessel_position') {
    if (raw?.military === true || raw?.isMilitary === true) return true;
    const shipType = raw?.shipType as number | undefined;
    // AIS ship type 35 = Military ops, 55 = Law enforcement
    return shipType === 35 || shipType === 55;
  }
  if (event.type === 'flight_position') {
    if (raw?.military === true || raw?.isMilitary === true) return true;
    const callsign = (raw?.callsign ?? raw?.call ?? '') as string;
    return Boolean(callsign) && isMilitaryCallsign(callsign);
  }

  // Text events: tags check
  const tags = event.tags ?? [];
  for (const tag of tags) {
    if (MILITARY_KEYWORDS.has(tag.toLowerCase())) return true;
  }

  return raw?.military === true || raw?.isMilitary === true;
}

/** Common military callsign prefixes. */
const MIL_CALLSIGN_PREFIXES = [
  'RCH', 'REACH', 'TOPCAT', 'HEAVY', 'SPAR', 'EVAC',
  'DUKE', 'KNIFE', 'HAVOC', 'VIPER', 'BOXER', 'DRACO',
  'SAM', 'EXEC', 'ROCKY', 'STEEL', 'IRON', 'COBRA',
  'RAVEN', 'HAWK', 'EAGLE', 'WOLF', 'RHINO', 'NATO',
  'FORTE', 'JAKE', 'LAGR', 'NCHO', 'CNV', 'RRR',
];

function isMilitaryCallsign(callsign: string): boolean {
  const upper = callsign.toUpperCase().trim();
  return MIL_CALLSIGN_PREFIXES.some((p) => upper.startsWith(p));
}

// ---------------------------------------------------------------------------
// Grid cell aggregation
// ---------------------------------------------------------------------------

interface ActivityCell {
  /** Grid cell center lat. */
  lat: number;
  /** Grid cell center lng. */
  lng: number;
  /** Number of military events in this cell. */
  count: number;
  /** Polygon vertices for the cell boundary. */
  polygon: [number, number][];
}

/** Aggregate events into 1-degree grid cells. */
function aggregateToGrid(events: NormalizedEvent[]): ActivityCell[] {
  const cells = new Map<string, { lat: number; lng: number; count: number }>();

  for (const e of events) {
    if (!e.location) continue;

    // Floor to 1-degree grid
    const gridLat = Math.floor(e.location.lat);
    const gridLng = Math.floor(e.location.lng);
    const key = `${gridLat},${gridLng}`;

    const existing = cells.get(key);
    if (existing) {
      existing.count++;
    } else {
      cells.set(key, {
        lat: gridLat + 0.5, // center of cell
        lng: gridLng + 0.5,
        count: 1,
      });
    }
  }

  const result: ActivityCell[] = [];
  for (const cell of cells.values()) {
    // Only show cells with 2+ events to reduce noise
    if (cell.count < 2) continue;

    const halfDeg = 0.5;
    result.push({
      lat: cell.lat,
      lng: cell.lng,
      count: cell.count,
      polygon: [
        [cell.lng - halfDeg, cell.lat - halfDeg],
        [cell.lng + halfDeg, cell.lat - halfDeg],
        [cell.lng + halfDeg, cell.lat + halfDeg],
        [cell.lng - halfDeg, cell.lat + halfDeg],
        [cell.lng - halfDeg, cell.lat - halfDeg], // close the ring
      ],
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Color mapping
// ---------------------------------------------------------------------------

/** Intensity color: more events = brighter red/orange. */
function getActivityColor(count: number): [number, number, number, number] {
  if (count >= 20) return [239, 68, 68, 120];   // bright red
  if (count >= 10) return [249, 115, 22, 100];  // orange
  if (count >= 5) return [234, 179, 8, 80];     // amber
  return [100, 116, 139, 60];                   // slate
}

// ---------------------------------------------------------------------------
// Layer creation
// ---------------------------------------------------------------------------

/** Create deck.gl layers for military activity zones. */
export function createMilitaryActivityLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  // Filter to military-related events
  const militaryEvents = events.filter(isMilitaryEvent);
  if (militaryEvents.length === 0) return [];

  const cells = aggregateToGrid(militaryEvents);
  if (cells.length === 0) return [];

  const layers: Layer[] = [];

  // Grid fill polygons
  layers.push(new PolygonLayer<ActivityCell>({
    id: 'military-activity-grid',
    data: cells,
    pickable: true,
    filled: true,
    stroked: true,
    lineWidthMinPixels: 1,
    getPolygon: (d) => d.polygon,
    getFillColor: (d) => getActivityColor(d.count),
    getLineColor: [255, 255, 255, 30],
    getLineWidth: 1,
    opacity: Math.min(0.8, 0.3 + zoom * 0.05),
  }));

  // Center dots showing event count at higher zooms
  if (zoom >= 5) {
    layers.push(new ScatterplotLayer<ActivityCell>({
      id: 'military-activity-center',
      data: cells,
      pickable: false,
      filled: true,
      radiusUnits: 'pixels' as const,
      getPosition: (d) => [d.lng, d.lat],
      getFillColor: (d) => {
        const base = getActivityColor(d.count);
        return [base[0], base[1], base[2], 200] as [number, number, number, number];
      },
      getRadius: (d) => 3 + Math.min(d.count, 20),
      opacity: 0.8,
    }));
  }

  return layers;
}
